import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Dialog type determines icon and color scheme
 */
export type ConfirmationDialogType = 'danger' | 'warning' | 'info';

/**
 * Configuration options for the confirmation dialog
 */
export interface ConfirmDialogConfig {
  /** Dialog title */
  title: string;
  /** Main message content */
  message: string;
  /** Type determines icon and color scheme */
  type?: ConfirmationDialogType;
  /** Optional custom Lucide icon name for the header (e.g., 'circle-help') */
  icon?: string;
  /** Optional Lucide icon name shown inside the confirm button (e.g., 'trash-2') */
  actionIcon?: string;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /** Specific item name/identifier to display on its own line below the message */
  itemDisplayName?: string;
  /** Optional second message shown after the item display name */
  secondaryMessage?: string;
  /** Override confirm button color independently from the dialog type. Defaults to type-based color. */
  confirmButtonVariant?: 'primary' | 'danger';
}

/**
 * Confirmation Dialog Component
 *
 * Internal component used by ConfirmationDialogService.
 * Not meant to be used directly in templates.
 *
 * Features:
 * - Type-based styling (danger, warning, info)
 * - Icon support (Lucide icons)
 * - Keyboard support (Escape to close)
 * - Focus trapping
 * - ARIA attributes for accessibility
 */
@Component({
  selector: 'app-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
  styleUrls: ['./confirmation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfirmationDialogComponent implements OnInit, OnDestroy {
  @ViewChild('dialogPanel', { static: true })
  dialogPanel!: ElementRef<HTMLElement>;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** Dialog title */
  title = '';

  /** Main message content */
  message = '';

  /** Type determines icon and color scheme */
  type: ConfirmationDialogType = 'warning';

  /** Custom Lucide icon name for the header (e.g., 'circle-help') */
  customIcon?: string;

  /** Lucide icon name shown inside the confirm button (e.g., 'trash-2') */
  actionIcon = '';

  /** Confirm button text */
  confirmText = 'Confirm';

  /** Cancel button text */
  cancelText = 'Cancel';

  /** Specific item name/identifier shown on its own line below the message */
  itemDisplayName = '';

  /** Optional second message shown after the item display name */
  secondaryMessage = '';

  /** Override confirm button color. When undefined, falls back to type-based color. */
  confirmButtonVariant?: 'primary' | 'danger';

  /** Subject for dialog close events */
  afterClosed$ = new Subject<boolean>();

  /** Close handler set by service */
  closeHandler?: (confirmed: boolean) => void;

  /** Controls visibility for animations */
  isVisible = false;

  /** Reference to previously focused element for focus restoration */
  private previouslyFocusedElement: HTMLElement | null = null;

  /** Focusable element selectors for focus trapping */
  private readonly FOCUSABLE_SELECTORS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /**
   * Returns the appropriate Lucide icon name based on type or custom icon
   */
  get iconName(): string {
    if (this.customIcon) {
      return this.customIcon;
    }

    // Default icons per type
    const iconMap: Record<ConfirmationDialogType, string> = {
      danger: 'circle-help',
      warning: 'triangle-alert',
      info: 'info'
    };

    return iconMap[this.type];
  }

  /**
   * Returns the CSS class for the icon circle based on type
   */
  get iconCircleClass(): string {
    return `confirmation-dialog__icon-circle confirmation-dialog__icon-circle--${this.type}`;
  }

  ngOnInit(): void {
    // Store currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    // Prevent body scroll while dialog is open
    document.body.style.overflow = 'hidden';

    // Trigger entrance animation on next frame for smooth rendering
    requestAnimationFrame(() => {
      this.isVisible = true;
      this.cdr.markForCheck();
      // Focus the dialog after visibility is set
      this.focusFirstElement();
    });
  }

  ngOnDestroy(): void {
    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      setTimeout(() => {
        this.previouslyFocusedElement?.focus();
      }, 0);
    }
  }

  /**
   * Focus the first focusable element inside the dialog
   */
  private focusFirstElement(): void {
    const dialogElement = this.dialogPanel?.nativeElement;
    if (!dialogElement) return;

    const focusableElements = dialogElement.querySelectorAll<HTMLElement>(this.FOCUSABLE_SELECTORS);

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      dialogElement.focus();
    }
  }

  /**
   * Trap focus inside the dialog
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.handleTabKey(event);
    }
  }

  /**
   * Handle Tab key for focus trapping
   */
  private handleTabKey(event: KeyboardEvent): void {
    const dialogElement = this.dialogPanel?.nativeElement;
    if (!dialogElement) return;

    const focusableElements = dialogElement.querySelectorAll<HTMLElement>(this.FOCUSABLE_SELECTORS);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift+Tab on first element goes to last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }
    // Tab on last element goes to first
    else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  /**
   * Handle Escape key to close dialog
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.cancel();
  }

  /**
   * Handle backdrop click to close dialog
   */
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.cancel();
    }
  }

  /**
   * Confirm action and close dialog
   */
  confirm(): void {
    this.close(true);
  }

  /**
   * Cancel action and close dialog
   */
  cancel(): void {
    this.close(false);
  }

  /**
   * Close the dialog with animation
   */
  private close(confirmed: boolean): void {
    this.isVisible = false;
    this.cdr.markForCheck();

    // Wait for exit animation to complete
    setTimeout(() => {
      if (this.closeHandler) {
        this.closeHandler(confirmed);
      }
    }, 200);
  }
}
